// Reviewing an uploaded question paper before it is published.

import { getQuestionOptions, getCorrectOptionIds, getOptionExplanation } from './question';

export function reviewQuestionId(question) {
  return question?.id || question?._id || question?.questionId || null;
}

/**
 * The `answer` body for PATCH .../etl/jobs/{job}/questions/{question}.
 *
 * The API takes exactly one entry: the correct option. Confirming the option
 * already marked correct keeps its own explanation; switching to another option
 * clears it, because the stored text argued for the wrong answer. Pass
 * `explanation` to send edited text instead.
 */
export function answerCorrection(question, targetOptionId, { explanation } = {}) {
  const options = getQuestionOptions(question);
  const target = String(targetOptionId);
  const option = options.find((item) => String(item.id) === target);
  let text = '';
  if (typeof explanation === 'string') {
    text = explanation.trim();
  } else if (option && getCorrectOptionIds(question, options).has(option.id)) {
    text = getOptionExplanation(question, options, option.id);
  }
  return [{ optionId: target, isCorrect: true, explanation: text }];
}

// Merge the question the API returned after a review edit into the local copy.
// The response omits display-only fields (section and topic titles, source
// image), so keep those from the list payload.
export function mergeReviewedQuestion(question, updated) {
  if (!updated || typeof updated !== 'object') return question;
  return { ...question, ...updated };
}

function position(value) {
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

// Paper order: page, then position on the page. Questions without either keep
// their relative order after the placed ones. Returns a new array.
export function sortReviewQuestions(questions) {
  const list = Array.isArray(questions) ? [...questions] : [];
  const placed = list.some((q) => Number.isFinite(q?.pageNumber) || Number.isFinite(q?.questionIndex));
  if (!placed) return list;
  return list
    .map((question, index) => ({ question, index }))
    .sort((a, b) =>
      position(a.question?.pageNumber) - position(b.question?.pageNumber)
      || position(a.question?.questionIndex) - position(b.question?.questionIndex)
      || a.index - b.index)
    .map(({ question }) => question);
}

// Why an extraction job failed. The API sends `error`; `errorMessage` is the
// older spelling.
export function jobErrorMessage(job) {
  const value = [job?.error, job?.errorMessage].find((candidate) => typeof candidate === 'string' && candidate.trim());
  return value ? value.trim() : null;
}
